// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Checkbox, FormControlLabel } from '@mui/material';
import { documentsApi } from '@shared/data-access/api/engine/documents-api';
import type { Document } from '@shared/data-access/types/engine-types';
import { DocumentScope } from '@shared/data-access/types/engine-types';
import { ConfirmPopup, useDynamicDialog } from '@shared/ui-kit/components';
import { useSnackbar } from '@shared/ui-kit/providers/snackbar-provider';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getDocumentRelationCount,
  isDocumentResourceInUseError,
} from '../utils/document-has-relations';

export interface UseDeleteDocumentConfirmOptions {
  readonly onDeleted?: () => void;
}

export interface ConfirmDeleteDocumentOptions {
  readonly onClose?: () => void;
}

export const useDeleteDocumentConfirm = ({
  onDeleted,
}: UseDeleteDocumentConfirmOptions = {}) => {
  const { t } = useTranslation();
  const { openDialog, closeDialog } = useDynamicDialog();
  const { enqueueSnackbar } = useSnackbar();
  const [deleteDocument] = documentsApi.useDeleteDocumentMutation();

  const confirmDeleteDocument = useCallback(
    (document: Document, options?: ConfirmDeleteDocumentOptions) => {
      if (document.scope === DocumentScope.SilverAlpha) {
        enqueueSnackbar(
          t('documents.detail-drawer.delete-error', { ns: 'components' }),
          { variant: 'error' },
        );
        return;
      }

      const DeleteDocumentConfirmDialog = () => {
        const knownRelationCount = getDocumentRelationCount(document);
        const hasKnownRelations = knownRelationCount > 0;

        const [forceDelete, setForceDelete] = useState(hasKnownRelations);
        const [forceRequiredByApi, setForceRequiredByApi] = useState(false);
        const [isLoading, setIsLoading] = useState(false);

        const showForceCheckbox = hasKnownRelations || forceRequiredByApi;

        const handleConfirm = async () => {
          setIsLoading(true);

          try {
            await deleteDocument({
              document_id: document.id,
              ...(showForceCheckbox && forceDelete ? { force: true } : {}),
            }).unwrap();

            options?.onClose?.();
            closeDialog();
            onDeleted?.();
          } catch (error) {
            if (isDocumentResourceInUseError(error)) {
              setForceRequiredByApi(true);
              setForceDelete(true);
              return;
            }

            enqueueSnackbar(
              t('documents.detail-drawer.delete-error', { ns: 'components' }),
              { variant: 'error' },
            );
            closeDialog();
          } finally {
            setIsLoading(false);
          }
        };

        return (
          <ConfirmPopup
            title={t('documents.detail-drawer.delete-confirm-title', {
              ns: 'components',
            })}
            message={t('documents.detail-drawer.delete-confirm-message', {
              ns: 'components',
              title: document.title,
            })}
            confirmText={t('documents.detail-drawer.delete', { ns: 'components' })}
            onConfirm={handleConfirm}
            onCancel={closeDialog}
            isLoading={isLoading}
            icon="Trashcan"
          >
            {hasKnownRelations ? (
              <p className="mt-3 text-sm text-primary-medium">
                {t('documents.detail-drawer.delete-relations-warning', {
                  ns: 'components',
                  count: knownRelationCount,
                })}
              </p>
            ) : null}
            {forceRequiredByApi && !hasKnownRelations ? (
              <p className="mt-3 text-sm text-primary-medium">
                {t('documents.detail-drawer.delete-force-required', {
                  ns: 'components',
                })}
              </p>
            ) : null}
            {showForceCheckbox ? (
              <FormControlLabel
                className="!mx-0 !mt-4 items-start"
                control={
                  <Checkbox
                    checked={forceDelete}
                    onChange={event => setForceDelete(event.target.checked)}
                    data-testid="delete-document-force-checkbox"
                  />
                }
                label={
                  <span className="text-left text-sm text-primary-medium">
                    {t('documents.detail-drawer.delete-force-label', {
                      ns: 'components',
                    })}
                  </span>
                }
              />
            ) : null}
          </ConfirmPopup>
        );
      };

      openDialog(<DeleteDocumentConfirmDialog />, {
        showCloseButton: false,
        classes: { paper: '!w-[335px] !overflow-y-visible' },
      });
    },
    [closeDialog, deleteDocument, enqueueSnackbar, onDeleted, openDialog, t],
  );

  return { confirmDeleteDocument };
};
